import * as React from 'react';
import { cn } from '../../lib/utils';

interface TabsProps {
  value: string;
  onValueChange: (value: string) => void;
  children: React.ReactNode;
  className?: string;
}

function Tabs({ value, onValueChange, children, className }: TabsProps) {
  return (
    <div className={className} data-value={value} data-onchange={undefined}>
      {React.Children.map(children, (child) =>
        React.isValidElement(child) ? React.cloneElement(child as any, { _value: value, _onValueChange: onValueChange }) : child
      )}
    </div>
  );
}

function TabsList({ className, _value, _onValueChange, children, ...props }: React.HTMLAttributes<HTMLDivElement> & { _value?: string; _onValueChange?: any }) {
  return (
    <div className={cn('inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground', className)} {...props}>
      {React.Children.map(children, (child) =>
        React.isValidElement(child) ? React.cloneElement(child as any, { _value, _onValueChange }) : child
      )}
    </div>
  );
}

interface TabsTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  value: string;
  _value?: string;
  _onValueChange?: (v: string) => void;
}

function TabsTrigger({ value, _value, _onValueChange, className, ...props }: TabsTriggerProps) {
  const isActive = _value === value;
  return (
    <button
      className={cn(
        'inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        isActive ? 'bg-background text-foreground shadow-sm' : 'hover:bg-background/50',
        className
      )}
      onClick={() => _onValueChange?.(value)}
      {...props}
    />
  );
}

interface TabsContentProps extends React.HTMLAttributes<HTMLDivElement> {
  value: string;
  _value?: string;
  _onValueChange?: any;
  /** Inaktiven Reiter gemountet lassen (nur `hidden`), damit lokaler State erhalten bleibt. */
  keepMounted?: boolean;
}

function TabsContent({ value, _value, _onValueChange, keepMounted, className, ...props }: TabsContentProps) {
  const aktiv = _value === value;
  if (!aktiv && !keepMounted) return null;
  return <div hidden={!aktiv} className={cn('mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2', className)} {...props} />;
}

export { Tabs, TabsList, TabsTrigger, TabsContent };
